import axios from 'axios';

const API_BASE_URL = 'https://api.coingecko.com/api/v3';
const API_KEY = process.env.REACT_APP_COINGECKO_API_KEY || '';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  params: {
    'x_cg_demo_api_key': API_KEY
  }
});

// A robust mapping of common token symbols or IDs to CoinGecko API IDs.
// This avoids redundant search API calls and protects against search result changes.
const SYMBOL_TO_ID: { [key: string]: string } = {
  'btc': 'bitcoin',
  'bitcoin': 'bitcoin',
  'eth': 'ethereum',
  'ethereum': 'ethereum',
  'ada': 'cardano',
  'cardano': 'cardano',
  'link': 'chainlink',
  'chainlink': 'chainlink',
  'night': 'midnight-3',
  'midnight-3': 'midnight-3',
  'usdt': 'tether',
  'tether': 'tether',
  'usdc': 'usd-coin',
  'usd-coin': 'usd-coin',
  'sol': 'solana',
  'solana': 'solana',
  'dot': 'polkadot',
  'polkadot': 'polkadot',
  'xrp': 'ripple',
  'ripple': 'ripple',
  'doge': 'dogecoin',
  'dogecoin': 'dogecoin',
};

export const pingCoinGecko = async (): Promise<boolean> => {
  try {
    const response = await axiosInstance.get('/ping');
    return response.data.gecko_says === '(V3) To the Moon!';
  } catch (error) {
    console.error('Error pinging CoinGecko API:', error);
    return false;
  }
};

export const getCryptoPrice = async (cryptoId: string, currency: string = 'usd'): Promise<number | null> => {
  try {
    const resolvedId = SYMBOL_TO_ID[cryptoId.toLowerCase()] || cryptoId;
    const response = await axiosInstance.get('/simple/price', {
      params: {
        ids: resolvedId,
        vs_currencies: currency
      }
    });
    return response.data[resolvedId][currency];
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error fetching price for ${cryptoId}:`, error.response?.data || error.message);
    } else {
      console.error(`Error fetching price for ${cryptoId}:`, String(error));
    }
    return null;
  }
};

export const searchCrypto = async (query: string): Promise<any[]> => {
  try {
    const response = await axiosInstance.get('/search', {
      params: { query }
    });
    return response.data.coins;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error searching for "${query}":`, error.response?.data || error.message);
    } else {
      console.error(`Error searching for "${query}":`, String(error));
    }
    return [];
  }
};

export const getMultipleCryptoPrices = async (symbols: string[]): Promise<{ [key: string]: { price: number, price_change_percentage_24h: number } }> => {
  try {
    const validSymbols = symbols.filter(s => s && typeof s === 'string' && s.trim() !== '');

    if (validSymbols.length === 0) {
      console.warn('No valid symbols provided to getMultipleCryptoPrices');
      return {};
    }

    // Resolve symbols to IDs using cache map or fallback to search API
    const symbolToIdMap: { [symbol: string]: string | null } = {};

    await Promise.all(
      validSymbols.map(async (symbol) => {
        const lowerSymbol = symbol.toLowerCase();
        if (SYMBOL_TO_ID[lowerSymbol]) {
          symbolToIdMap[symbol] = SYMBOL_TO_ID[lowerSymbol];
          return;
        }

        try {
          const searchResult = await searchCrypto(symbol);
          if (searchResult && searchResult.length > 0) {
            // Try to find direct symbol match first to avoid wrong coin matches
            const exactMatch = searchResult.find(
              (coin: any) => coin.symbol.toLowerCase() === lowerSymbol
            );
            symbolToIdMap[symbol] = exactMatch ? exactMatch.id : searchResult[0].id;
          } else {
            symbolToIdMap[symbol] = null;
          }
        } catch (err) {
          console.error(`Error resolving symbol "${symbol}":`, err);
          symbolToIdMap[symbol] = null;
        }
      })
    );

    const coinIds = Object.values(symbolToIdMap).filter((id): id is string => !!id);

    if (coinIds.length === 0) {
      console.warn('No valid coin IDs resolved for symbols:', validSymbols);
      return {};
    }

    const response = await axiosInstance.get('/simple/price', {
      params: {
        ids: coinIds.join(','),
        vs_currencies: 'eur',
        include_24hr_change: true
      }
    });

    const prices: { [key: string]: { price: number, price_change_percentage_24h: number } } = {};
    
    validSymbols.forEach((symbol) => {
      const coinId = symbolToIdMap[symbol];
      if (coinId && response.data[coinId]) {
        const coinData = response.data[coinId];
        prices[symbol.toUpperCase()] = {
          price: coinData.eur,
          price_change_percentage_24h: coinData.eur_24h_change || 0
        };
      }
    });

    return prices;
  } catch (error) {
    console.error('Error fetching multiple crypto prices:', error);
    throw error;
  }
};

export interface TokenDetails {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price: number;
  price_change_percentage_24h: number;
}

export const getTokenDetails = async (symbol: string): Promise<TokenDetails | null> => {
  try {
    const lowerSymbol = symbol.toLowerCase();
    let coinId: string | null = null;

    if (SYMBOL_TO_ID[lowerSymbol]) {
      coinId = SYMBOL_TO_ID[lowerSymbol];
    } else {
      const searchResult = await searchCrypto(symbol);
      if (searchResult.length === 0) {
        throw new Error(`No results found for symbol: ${symbol}`);
      }
      const exactMatch = searchResult.find(
        (coin: any) => coin.symbol.toLowerCase() === lowerSymbol
      );
      coinId = exactMatch ? exactMatch.id : searchResult[0].id;
    }

    const response = await axiosInstance.get(`/coins/${coinId}`, {
      params: {
        localization: false,
        tickers: false,
        market_data: true,
        community_data: false,
        developer_data: false,
        sparkline: false
      }
    });
    const data = response.data;
    return {
      id: data.id,
      symbol: data.symbol.toUpperCase(),
      name: data.name,
      image: data.image.small,
      current_price: data.market_data.current_price.eur,
      price_change_percentage_24h: data.market_data.price_change_percentage_24h
    };
  } catch (error) {
    console.error(`Error fetching details for token ${symbol}:`, error);
    return null;
  }
};